// CrystalVault — Hyperliquid state feed (shared cache, Tier-2-shaped interface).
//
// Purpose: every page in the vault frontend pulls Hyperliquid state via
// this single module instead of fetching directly. Guarantees that two
// pages loaded seconds apart see BYTE-IDENTICAL numbers so long as both
// read within the cache's TTL window.
//
// Interface (kept stable across future upgrades):
//   getVaultState(address)  → Promise<clearinghouseState>
//   getFills(address)       → Promise<userFills>
//   subscribe(addr, cb)     → returns unsubscribe()
//   refresh(address)        → force a fetch; resolves to fresh state
//
// Current implementation: REST-polled with TTL cache + in-flight dedup.
// When WebSocket push is enabled, only this file changes — pages are
// already subscribe-shaped. See memory/project_vault_hl_feed_architecture.md.

import { NETWORK } from './config.js';

// Routes /info calls through crystalclear-api's /api/hl/info proxy (which
// hits Chainstack server-side). This keeps the Chainstack auth URL out of
// the browser bundle and isolates us from HL public /info rate limits.
// Testnet still hits HL directly — no Chainstack testnet endpoint today.
const API_BASE = (typeof window !== 'undefined' && typeof window.CC_API_BASE === 'string')
  ? window.CC_API_BASE
  : '';
const HL_API_DIRECT = NETWORK.isProduction
  ? 'https://api.hyperliquid.xyz'
  : 'https://api.hyperliquid-testnet.xyz';
const USE_PROXY = NETWORK.isProduction;  // only mainnet has the proxy wired

// How long a fetched state/fills snapshot is considered fresh. Two pages
// loading within this window will render identical numbers. Tuned to
// balance freshness (smaller = livelier) against API load + divergence
// (bigger = fewer fetches + tighter cross-page consistency).
const TTL_MS = 3000;

// Per-address cache entry shape:
//   {
//     state: {...},           // last fetched clearinghouseState (or null)
//     fills: [...],           // last fetched userFills (or null)
//     stateAt: number,        // ms since epoch when state was fetched
//     fillsAt: number,        // ms since epoch when fills were fetched
//     stateInFlight: Promise, // dedup for concurrent getState() calls
//     fillsInFlight: Promise,
//   }
const _cache = new Map();

// Per-address subscriber list. Subscribers get notified whenever the
// feed has new data for their address.
const _subscribers = new Map();

// ── Internal helpers ─────────────────────────────────────────────────────

function _entry(addr) {
  let e = _cache.get(addr);
  if (!e) {
    e = { state: null, fills: null, stateAt: 0, fillsAt: 0, stateInFlight: null, fillsInFlight: null };
    _cache.set(addr, e);
  }
  return e;
}

function _notify(addr, payload) {
  const subs = _subscribers.get(addr);
  if (!subs) return;
  for (const cb of subs) {
    try { cb(payload); } catch (err) { console.error('hlFeed subscriber threw:', err); }
  }
}

async function _fetchJson(path, body) {
  // On mainnet, /info goes through our proxy (Chainstack-backed).
  // /exchange and anything else falls through to HL direct.
  const url = (USE_PROXY && path === '/info')
    ? API_BASE + '/api/hl/info'
    : HL_API_DIRECT + path;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HL ${path} HTTP ${res.status}`);
  return res.json();
}

async function _doFetchState(addr) {
  const e = _entry(addr);
  try {
    const state = await _fetchJson('/info', { type: 'clearinghouseState', user: addr });
    e.state = state;
    e.stateAt = Date.now();
    _notify(addr, { kind: 'state', state });
    return state;
  } finally {
    e.stateInFlight = null;
  }
}

async function _doFetchFills(addr) {
  const e = _entry(addr);
  try {
    const fills = await _fetchJson('/info', { type: 'userFills', user: addr });
    e.fills = fills || [];
    e.fillsAt = Date.now();
    _notify(addr, { kind: 'fills', fills: e.fills });
    return e.fills;
  } finally {
    e.fillsInFlight = null;
  }
}

// ── Public API ───────────────────────────────────────────────────────────

/**
 * Returns HL clearinghouseState for the vault address.
 * Cached for TTL_MS. Concurrent callers within TTL see identical data
 * and share a single in-flight fetch (dedup).
 */
export async function getVaultState(address) {
  const addr = address.toLowerCase();
  const e = _entry(addr);
  const now = Date.now();
  if (e.state && now - e.stateAt < TTL_MS) return e.state;
  if (e.stateInFlight) return e.stateInFlight;
  e.stateInFlight = _doFetchState(addr);
  return e.stateInFlight;
}

/**
 * Prefetch HL clearinghouseState for multiple vault addresses in one
 * round trip. Use this BEFORE a batch of getVaultState() calls so they
 * all hit the cache and skip the per-address fan-out.
 *
 * Replaces N parallel POSTs to /api/hl/info (each spawning a cold
 * Lambda spillover) with ONE POST to /api/fleet/hl-state — the Lambda
 * fans the upstream HL calls out in-process.
 *
 * Falls back to the per-address path on any error, so callers don't
 * need a try/catch around this — getVaultState() will still work.
 *
 * Returns a promise that resolves once cache is populated. Resolved
 * value is the same shape as Promise.all(addresses.map(getVaultState)).
 */
export async function prefetchVaultStates(addresses) {
  if (!USE_PROXY) {
    // Testnet / dev — no proxy, fall back to per-address path.
    return Promise.all(addresses.map(getVaultState));
  }
  const addrs = addresses.map(a => a.toLowerCase());
  // If everything is already cached and fresh, skip the network entirely.
  const now = Date.now();
  const stale = addrs.filter(a => {
    const e = _entry(a);
    return !e.state || now - e.stateAt >= TTL_MS;
  });
  if (stale.length === 0) {
    return addrs.map(a => _entry(a).state);
  }
  try {
    const res = await fetch(API_BASE + '/api/fleet/hl-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addrs: stale }),
    });
    if (!res.ok) throw new Error(`fleet/hl-state HTTP ${res.status}`);
    const body = await res.json();
    const byAddr = body.addrs || {};
    // Populate cache from batched response. Match server-returned keys
    // case-insensitively since EIP-55 mixed case is preserved on the wire.
    for (const [serverAddr, state] of Object.entries(byAddr)) {
      const key = serverAddr.toLowerCase();
      if (state && !state.error) {
        const e = _entry(key);
        e.state = state;
        e.stateAt = Date.now();
        _notify(key, { kind: 'state', state });
      }
    }
  } catch (err) {
    console.warn('prefetchVaultStates failed, falling back to per-addr:', err);
    // Fall back to individual getVaultState calls.
    return Promise.all(addrs.map(getVaultState));
  }
  // Return state for each address (now warmed in cache, or fetched fresh
  // on cache miss for addrs not in the batch response).
  return Promise.all(addrs.map(getVaultState));
}

/**
 * Prefetch HL userFills for multiple vault addresses in ONE round trip.
 * Same idea as prefetchVaultStates but for fills: replaces N parallel
 * POSTs to /api/hl/info (userFills) — each spawning a cold Lambda
 * spillover — with ONE POST to /api/fleet/fills (server fans out
 * in-process). Populates the per-address fills cache so subsequent
 * getFills() calls are cache hits.
 *
 * Falls back to the per-address path on any error, so callers don't need
 * a try/catch — getFills() still works.
 */
export async function prefetchFills(addresses) {
  if (!USE_PROXY) {
    return Promise.all(addresses.map(getFills));
  }
  const addrs = addresses.map(a => a.toLowerCase());
  const now = Date.now();
  const stale = addrs.filter(a => {
    const e = _entry(a);
    return !e.fills || now - e.fillsAt >= TTL_MS;
  });
  if (stale.length === 0) {
    return addrs.map(a => _entry(a).fills);
  }
  try {
    const res = await fetch(API_BASE + '/api/fleet/fills', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addrs: stale }),
    });
    if (!res.ok) throw new Error(`fleet/fills HTTP ${res.status}`);
    const body = await res.json();
    const byAddr = body.addrs || {};
    for (const [serverAddr, fills] of Object.entries(byAddr)) {
      if (!Array.isArray(fills)) continue;  // skip {error:…} entries
      const key = serverAddr.toLowerCase();
      const e = _entry(key);
      e.fills = fills;
      e.fillsAt = Date.now();
      _notify(key, { kind: 'fills', fills });
    }
  } catch (err) {
    console.warn('prefetchFills failed, falling back to per-addr:', err);
    return Promise.all(addrs.map(getFills));
  }
  return Promise.all(addrs.map(getFills));
}

/**
 * Seed the per-address state cache from an already-fetched
 * /api/fleet/hl-state response. Used by the combined /api/me/overview
 * endpoint to avoid a second network round-trip for prefetchVaultStates.
 *
 * Shape of `byAddr` matches the `addrs` field of /api/fleet/hl-state:
 *   { "0x…": { marginSummary, assetPositions, … }, … }
 *
 * Server-returned addresses preserve EIP-55 mixed-case; we lowercase
 * for cache keys to match the rest of the module.
 */
export function seedFleetState(byAddr) {
  if (!byAddr || typeof byAddr !== "object") return;
  for (const [serverAddr, state] of Object.entries(byAddr)) {
    if (!state || state.error) continue;
    const key = serverAddr.toLowerCase();
    const e = _entry(key);
    e.state = state;
    e.stateAt = Date.now();
    _notify(key, { kind: "state", state });
  }
}

/**
 * Returns HL userFills for the vault address. Same caching semantics as
 * getVaultState.
 */
export async function getFills(address) {
  const addr = address.toLowerCase();
  const e = _entry(addr);
  const now = Date.now();
  if (e.fills && now - e.fillsAt < TTL_MS) return e.fills;
  if (e.fillsInFlight) return e.fillsInFlight;
  e.fillsInFlight = _doFetchFills(addr);
  return e.fillsInFlight;
}

/**
 * Force a fresh fetch regardless of TTL. Use for user-triggered refresh
 * buttons. Resolves to the fresh state.
 */
export async function refresh(address) {
  const addr = address.toLowerCase();
  const e = _entry(addr);
  e.stateAt = 0;
  e.fillsAt = 0;
  return Promise.all([getVaultState(addr), getFills(addr)]);
}

/**
 * Subscribe to updates for a given vault address. Callback fires whenever
 * the cache is refreshed (from any source: getVaultState/getFills call,
 * refresh(), or the background poller).
 *
 * Returns an unsubscribe() function.
 *
 * Callback signature: ({ kind: 'state' | 'fills', state?: object, fills?: array })
 */
export function subscribe(address, callback) {
  const addr = address.toLowerCase();
  if (!_subscribers.has(addr)) _subscribers.set(addr, new Set());
  _subscribers.get(addr).add(callback);
  _ensurePolling();
  return () => {
    const subs = _subscribers.get(addr);
    if (subs) {
      subs.delete(callback);
      if (subs.size === 0) _subscribers.delete(addr);
    }
  };
}

// ── Background poller ────────────────────────────────────────────────────
// When at least one address has subscribers, poll every TTL_MS to keep
// state fresh. Pages that subscribed get push-style updates without
// having to call getVaultState on a timer themselves.

let _pollHandle = null;

function _ensurePolling() {
  if (_pollHandle) return;
  _pollHandle = setInterval(_pollTick, TTL_MS);
}

function _pollTick() {
  if (_subscribers.size === 0) {
    clearInterval(_pollHandle);
    _pollHandle = null;
    return;
  }
  // Respect Page Visibility API — don't hammer the API from a hidden tab.
  if (document.visibilityState === 'hidden') return;

  for (const addr of _subscribers.keys()) {
    _doFetchState(addr).catch((e) => console.warn('hlFeed state poll failed', addr, e));
    // Fills change less often — poll half as fast
    const ent = _entry(addr);
    if (Date.now() - ent.fillsAt > TTL_MS * 2) {
      _doFetchFills(addr).catch((e) => console.warn('hlFeed fills poll failed', addr, e));
    }
  }
}
